import { useCallback, useEffect, useState } from "react";
import handleService from "../services/apiService.ts";

export interface Entidade {
  id: string;
  nome: string;
  [key: string]: any;
}

export interface CirurgiaOpcoes {
  medicos: Entidade[];
  pacientes: Entidade[];
  hospitais: Entidade[];
  instrumentadores: Entidade[];
  convenios: Entidade[];
  tipoCirurgias: Entidade[];
}

type Chave = keyof CirurgiaOpcoes;

const OPCOES_VAZIAS: CirurgiaOpcoes = {
  medicos: [],
  pacientes: [],
  hospitais: [],
  instrumentadores: [],
  convenios: [],
  tipoCirurgias: [],
};

const ENDPOINT_POR_CHAVE: Record<Chave, string> = {
  medicos: "/medico",
  pacientes: "/paciente",
  hospitais: "/hospital",
  instrumentadores: "/instrumentador",
  convenios: "/convenio",
  tipoCirurgias: "/tipo-cirurgia",
};

const TTL_MS = 5 * 60 * 1000;

// Sem isso uma requisicao presa na fila do pool de conexoes do backend deixa o
// drawer em "Carregando dados..." indefinidamente. 20s e folgado pra uma
// listagem simples e curto o bastante pra virar erro visivel em vez de tela
// travada.
const TIMEOUT_MS = 20_000;

// Cache em escopo de módulo (singleton natural de ES module) — compartilhado
// entre NovaCirurgiaForm e CirurgiaDetailsModal, sem precisar de Context novo.
// Só existe pra esses 2 consumidores; nenhum outro lugar do sistema precisa
// dessas 6 listas juntas.
let cache: { data: CirurgiaOpcoes; timestamp: number } | null = null;
let inflight: Promise<ResultadoOpcoes> | null = null;
const listeners = new Set<() => void>();

const notificar = () => listeners.forEach((l) => l());

const ordenar = (lista: Entidade[]) => [...lista].sort((a, b) => a.nome.localeCompare(b.nome));

export interface ResultadoOpcoes {
  dados: CirurgiaOpcoes;
  /** Chaves cujo endpoint falhou. Vazio = as 6 listas vieram completas. */
  falhas: Chave[];
}

const buscarTudo = async (): Promise<ResultadoOpcoes> => {
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const chaves = Object.keys(ENDPOINT_POR_CHAVE) as Chave[];
      const respostas = await Promise.all(
        chaves.map(async (chave) => {
          try {
            const r = await handleService({
              endpoint: ENDPOINT_POR_CHAVE[chave],
              timeout: TIMEOUT_MS,
            });
            return { lista: Array.isArray(r.data) ? ordenar(r.data) : [], ok: true };
          } catch {
            return { lista: [] as Entidade[], ok: false };
          }
        })
      );
      const dados = chaves.reduce((acc, chave, i) => {
        acc[chave] = respostas[i].lista;
        return acc;
      }, {} as CirurgiaOpcoes);

      const falhas = chaves.filter((_, i) => !respostas[i].ok);

      // So cacheia resultado completo. Antes, uma lista que falhasse virava []
      // e esse [] ficava valendo 5 minutos — o usuario via um dropdown vazio,
      // sem erro nenhum, e nem recarregar a tela resolvia dentro do TTL.
      if (falhas.length === 0) {
        cache = { data: dados, timestamp: Date.now() };
      }

      return { dados, falhas };
    } finally {
      inflight = null;
    }
  })();

  return inflight;
};

const cacheValido = () => !!cache && Date.now() - cache.timestamp < TTL_MS;

/** Adiciona um item recém-cadastrado (via modal de "novo médico/paciente/..."
 * embutido no form) direto no cache compartilhado — assim ele já aparece se o
 * usuário abrir "Editar cirurgia" logo em seguida, sem esperar o TTL. */
export const adicionarOpcaoLocal = (chave: Chave, item: Entidade) => {
  const base = cache?.data ?? OPCOES_VAZIAS;
  cache = {
    data: { ...base, [chave]: ordenar([...base[chave], item]) },
    timestamp: cache?.timestamp ?? Date.now(),
  };
  notificar();
};

export const invalidarCirurgiaOpcoesCache = () => {
  cache = null;
};

/** Hook pra popular os dropdowns de Médico/Paciente/Hospital/Instrumentador/
 * Convênio/Tipo de cirurgia usados em "Nova cirurgia" e "Editar cirurgia".
 * Cacheia em módulo por 5min — evita refazer as mesmas 6 chamadas toda vez
 * que um desses drawers abre. */
export const useCirurgiaOpcoes = () => {
  const [opcoes, setOpcoes] = useState<CirurgiaOpcoes>(cache?.data ?? OPCOES_VAZIAS);
  const [carregando, setCarregando] = useState(!cacheValido());
  const [falhas, setFalhas] = useState<Chave[]>([]);

  useEffect(() => {
    const listener = () => {
      if (cache) setOpcoes(cache.data);
    };
    listeners.add(listener);

    if (cacheValido()) {
      setOpcoes(cache!.data);
      setFalhas([]);
      setCarregando(false);
    } else {
      setCarregando(true);
      buscarTudo().then(({ dados, falhas: erros }) => {
        setOpcoes(dados);
        setFalhas(erros);
        setCarregando(false);
      });
    }

    return () => {
      listeners.delete(listener);
    };
  }, []);

  const adicionarLocal = useCallback((chave: Chave, item: Entidade) => {
    adicionarOpcaoLocal(chave, item);
  }, []);

  const refetch = useCallback(async () => {
    cache = null;
    setCarregando(true);
    const { dados, falhas: erros } = await buscarTudo();
    setOpcoes(dados);
    setFalhas(erros);
    setCarregando(false);
  }, []);

  return { opcoes, carregando, falhas, adicionarLocal, refetch };
};
